// The Krakens on the map: King Octo + Queen Octo in the deep channel. Tapped → the strike clip plays full-size over the map
// (like the HOARD chest), the Krakens window opens the moment the lightning has flashed, and the clip shrinks back into their spot.
// Plus the "Find the Krakens" badge that flies the map to them.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { useKrakon, useKrakonData, KRAKON_IMG, abs } from "./KrakonDock";
import { troveMuted } from "../hoard/chest";

const STRIKE_MP4 = "/krakon-strike.mp4"; // trimmed to 3.1 s — the rise + the strike, none of the extra emerging
const STRIKE_WEBM = "/krakon-strike.webm";
const POSTER = "/krakon-surface.webp";
const SURFACED = "/krakon-badge.webp";
const FLASH_DONE = 3.05; // ffmpeg luma analysis: the lightning flashes 2.83–3.04 s (peak 2.96 s) — open the window as it fades

const RING = "border-radius:50%;object-fit:cover;display:block;outline:2px solid #7FE3D6;box-shadow:0 6px 20px rgba(0,0,0,.55),0 0 0 4px rgba(127,227,214,.15)";
const LABEL = { idle: "Meet the Krakens", surfacing: "Surfacing…", surfaced: "Surfaced" };

const icon = (state, badges) => {
  const s = state === "idle" ? 52 : 60;
  const two = badges.length > 1;
  const w = two ? Math.round(s * 1.62) : s;
  const imgs = badges.slice(0, 2).map((b, i) => `<img src="${b}" alt="" style="position:absolute;top:0;left:${i ? w - s : 0}px;width:${s}px;height:${s}px;${RING};z-index:${2 - i}"/>`).join("");
  return L.divIcon({
    className: "",
    html: `<div data-testid="krakon-marker" data-state="${state}" style="width:${w}px;height:${s}px;position:relative;cursor:pointer;${state === "idle" ? "animation:krakon-bob 3.2s ease-in-out infinite" : ""}">${imgs}
      <span style="position:absolute;left:50%;bottom:-15px;transform:translateX(-50%);white-space:nowrap;padding:1px 7px;border-radius:999px;background:rgba(6,32,43,.92);color:#7FE3D6;font:800 7.5px/1.3 Manrope,sans-serif;letter-spacing:.15em;text-transform:uppercase;z-index:3">${LABEL[state]}</span></div>`,
    iconSize: [w, s], iconAnchor: [w / 2, s / 2],
  });
};

// Full-map strike: plays big, then (phase "returning") glides down into the marker's spot and fades. Tapping it skips to the window.
const KrakonStrike = ({ container, phase, target, onFlashed, onDone }) => {
  const vid = useRef(null);
  const box = useRef(null);
  useEffect(() => { if (box.current) { L.DomEvent.disableClickPropagation(box.current); L.DomEvent.disableScrollPropagation(box.current); } }, []);
  useEffect(() => {
    const v = vid.current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play?.();
    if (p?.catch) p.catch(onFlashed);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const back = phase === "returning";
  const style = back && target ? { left: target.x - 55, top: target.y - 31, width: 110, height: 62, opacity: 0 } : { left: 0, top: 0, width: "100%", height: "100%", opacity: 1 };
  return createPortal(
    <div ref={box} style={style} data-testid="krakon-strike" data-phase={phase} onClick={back ? undefined : onFlashed}
      className={`absolute z-[1200] overflow-hidden rounded-xl bg-black ring-2 ring-[#7FE3D6] shadow-2xl transition-[left,top,width,height,opacity] duration-500 ease-in-out ${back ? "pointer-events-none" : ""}`}
      onTransitionEnd={(e) => { if (back && e.propertyName === "width") onDone(); }}>
      <video ref={vid} poster={POSTER} muted playsInline preload="auto" disablePictureInPicture
        onTimeUpdate={(e) => { if (e.currentTarget.currentTime >= FLASH_DONE) onFlashed(); }} onEnded={onFlashed}
        className="h-full w-full object-cover object-[58%_50%] sm:object-contain" data-testid="krakon-strike-video">
        <source src={STRIKE_MP4} type="video/mp4" />
        <source src={STRIKE_WEBM} type="video/webm" />
      </video>
    </div>,
    container,
  );
};

export const KrakonMarker = () => {
  const data = useKrakonData();
  const k = useKrakon();
  const [state, setState] = useState("idle"); // idle | surfacing | surfaced
  const [strike, setStrike] = useState(null); // null | { phase: "playing" | "returning", target }
  const flashed = useRef(false);
  const timer = useRef(null);
  const map = useMap();
  useEffect(() => { if (!k.open && state === "surfaced") setState("idle"); }, [k.open, state]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onFlashed = () => {
    if (flashed.current) return;
    flashed.current = true;
    clearTimeout(timer.current);
    k.openDock(); // the window first — nothing below may stop it opening
    setState("surfaced");
    let target = null;
    try { target = data?.spot ? map.latLngToContainerPoint([data.spot.lat, data.spot.lng]) : null; } catch { /* map moved away */ }
    setStrike({ phase: "returning", target });
    timer.current = setTimeout(() => setStrike(null), 900);
  };
  const tap = () => {
    if (state === "surfacing") return;
    if (state === "surfaced") { k.openDock(); return; }
    flashed.current = false;
    setState("surfacing");
    setStrike({ phase: "playing", target: null });
    if (!troveMuted() && !window.__wallVoicePlaying) { try { const a = new Audio("/sounds/krakon.mp3"); a.volume = 0.7; a.play().catch(() => {}); } catch { /* no audio */ } }
    timer.current = setTimeout(onFlashed, (FLASH_DONE + 1.5) * 1000); // safety net if the clip can't play
  };
  // "wall:find-krakon": fly to today's spot
  useEffect(() => {
    if (!data?.spot) return;
    const h = () => { map.flyTo([data.spot.lat, data.spot.lng], Math.max(map.getZoom(), 11), { duration: 1.1 }); toast(`The Krakens keep to ${data.spot.name}`, { description: "Tap them to meet King Octo and Queen Octo." }); };
    window.addEventListener("wall:find-krakon", h);
    return () => window.removeEventListener("wall:find-krakon", h);
  }, [data, map]);
  if (!data?.spot) return null;
  const badges = (data.krakens || []).map((x) => abs(x.badge_url)).filter(Boolean);
  return (
    <>
      <Marker position={[data.spot.lat, data.spot.lng]} icon={icon(state, badges.length ? badges : [SURFACED])} zIndexOffset={1200} eventHandlers={{ click: tap }} />
      {strike && <KrakonStrike container={map.getContainer().closest("[data-testid=happening-map-leaflet]") || map.getContainer()} phase={strike.phase} target={strike.target} onFlashed={onFlashed} onDone={() => setStrike(null)} />}
    </>
  );
};

export const FindKrakonButton = () => (
  <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("wall:find-krakon"))} title="Find the Krakens — King Octo and Queen Octo"
    className="absolute top-2 right-2 z-[500] group flex flex-col items-center gap-0.5 border-0 bg-transparent p-0 cursor-pointer" data-testid="find-krakon-btn" data-insight="Map · Find the Krakon">
    <img src={KRAKON_IMG} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#7FE3D6] shadow-xl transition-transform group-hover:scale-105 group-active:scale-95" />
    <span className="rounded-full bg-[#06202B]/85 px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.18em] text-[#7FE3D6]">Find the Krakens</span>
  </button>
);
